import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardDialogFramePart, DialogAction, DialogContext } from '@eforall/dashboard';
import { LanguageId, SpecialistAvailabilityResult, SpecialistSignUpAddData } from '@interfaces';
import { DomainDataService, FuncService } from '../../../../../services';
import { PersonFinderWidget } from '../../../search';

@Component({
	selector: 'specialist-sign-up-add-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		PersonFinderWidget,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './specialist-sign-up-add.dialog.html'
})

export class SpecialistSignUpAddDialog {

	public dialogRef = inject(MatDialogRef<SpecialistSignUpAddDialog>);
	public dialogContext: DialogContext<SpecialistSignUpAddData> = inject(MAT_DIALOG_DATA);

	private func = inject(FuncService);
	private domainDataService = inject(DomainDataService);

	public selectedPersonId: number | undefined = undefined;
	public specialistAvailability = signal<SpecialistAvailabilityResult | undefined>(undefined);

	public dialogData = computed(() => {
		const data = this.dialogContext.data;
		if (!data) return undefined;

		return {
			...data,
			topicName: this.domainDataService.data().topicsMap[data.topicId]?.name,
			language: data.languageId === LanguageId.Espanol ? 'Spanish' : 'English'
		};
	})

	public actions = computed<DialogAction[]>(() => {
		const actions: DialogAction[] = [];

		if (!this.dialogContext.data) return actions;

		const specialistAvailability = this.specialistAvailability();

		const canAdd = specialistAvailability && specialistAvailability?.volunteerId && !specialistAvailability?.approvalIssues;

		if (!canAdd) {
			actions.push({
				id: 'check',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Check'),
				linkType: 'callback',
				callback: async () => await this.checkSpecialistAvailability(),
				willCloseDialog: false,
			});
		} else {
			actions.push({
				id: 'add',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Add Signup'),
				linkType: 'callback',
				callback: async () => await this.addSpecialistSignup(),
				willCloseDialog: true,
			});
		}

		return [
			...actions,
			{
				id: 'close',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Close'),
				linkType: "no-action",
				willCloseDialog: true,
			},
		]
	});

	setSelectedPersonId(personId: number) {
		this.selectedPersonId = personId;
		this.specialistAvailability.set(undefined);
	}

	async checkSpecialistAvailability() {
		const dialogData = this.dialogContext.data;
		if (!this.selectedPersonId || !dialogData) return;

		const payload = {
			personId: this.selectedPersonId,
			topicId: dialogData.topicId,
			languageId: dialogData.languageId,
			checkForExistingSignUp: {
				trimesterId: dialogData.trimesterId,
				eventStartUTC: dialogData.availableClassStartTimeUTC,
				eventEndUTC: dialogData.availableClassEndTimeUTC
			}
		};

		const data = await this.func.volunteering.specialistSignUp.checkSpecialistAvailability(payload);
		this.specialistAvailability.set(data);
	}

	async addSpecialistSignup() {
		const data = this.dialogContext.data;
		const availabilityResult = this.specialistAvailability();
		if (!this.selectedPersonId || !data || !availabilityResult || !availabilityResult.volunteerId || availabilityResult.approvalIssues || availabilityResult.hasExistingLanguageSignUpForTopic) return;

		await this.func.volunteering.specialistSignUp.addSignUp({
			volunteerId: availabilityResult.volunteerId,
			specialistTopicSignupData: data
		});
	}

}